import React, { useState, useRef, useEffect } from "react";
import "./styles/base.css";
import "./styles/youth.css";

export default function YouthForm({ age, onComplete }) {
  const STORAGE_KEY = "youthForm_v1";
  const warningRef = useRef(null);
  const allergyRef = useRef(null);

  // Initialize formData from localStorage synchronously on first render
  const [formData, setFormData] = useState(() => {
    const initial = {
      age: age || "",
      firstName: "",
      lastName: "",
      dob: "",
      email: "",
      phone: "",
      dormChoice: "",
      stayingWith: "",
      emergency1Name: "",
      emergency1Phone: "",
      emergency2Name: "",
      emergency2Phone: "",
      hasAllergies: "",
      allergies: "",
      allergyAcknowledged: false, // added acknowledgement flag
      takesMedications: "",
      medications: "",

      // honeypot (should remain empty)
      website: "",
    };
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...initial, ...JSON.parse(saved) };
      }
    } catch (e) {
      // ignore parse errors
    }
    return initial;
  });

  // Persist formData to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      // ignore storage errors
    }
  }, [formData]);

  // Derived UI state: compute med warning each render (no setState in effect)
  const inDorm = formData.dormChoice === "girls" || formData.dormChoice === "boys";
  const showMedWarning = inDorm && formData.takesMedications === "yes";

  // Keep acknowledgement state for meds and reset it only in event handlers when controlling values change
  const [medsAcknowledged, setMedsAcknowledged] = useState(false);

  function formatDobMMDDYYYY(input) {
    const digits = String(input || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    if (name === "dob") {
      const formatted = formatDobMMDDYYYY(newValue);
      setFormData((prev) => ({ ...prev, dob: formatted }));
      return;
    }

    if (name === "dormChoice") {
      setFormData((prev) => ({
        ...prev,
        dormChoice: newValue,
        stayingWith: newValue === "parent" ? prev.stayingWith : "",
      }));
      setMedsAcknowledged(false);
      return;
    }

    if (name === "takesMedications") {
      setFormData((prev) => ({
        ...prev,
        takesMedications: newValue,
        medications: newValue === "yes" ? prev.medications : "",
      }));
      setMedsAcknowledged(false);
      return;
    }

    if (name === "hasAllergies") {
      setFormData((prev) => ({
        ...prev,
        hasAllergies: newValue,
        // if user did not select yes, clear allergies and acknowledgement
        allergies: newValue === "yes" ? prev.allergies : "",
        allergyAcknowledged: newValue === "yes" ? prev.allergyAcknowledged : false,
      }));
      return;
    }

    // Default update (covers allergies input and allergyAcknowledged checkbox)
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    // Honeypot: bots fill this, humans won't
    if (String(formData.website || "").trim()) {
      // Fail silently to avoid giving bots feedback
      return;
    }

    // If allergies present, require acknowledgement first
    if (formData.hasAllergies === "yes" && !formData.allergyAcknowledged) {
      if (allergyRef.current) {
        allergyRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        const checkbox = allergyRef.current.querySelector('input[name="allergyAcknowledged"]');
        if (checkbox) checkbox.focus();
      }
      return;
    }

    // Then check meds acknowledgement (existing behavior)
    if (showMedWarning && !medsAcknowledged) {
      if (warningRef.current) {
        warningRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        const checkbox = warningRef.current.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.focus();
      }
      return;
    }

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      // ignore
    }

    onComplete(formData);
  }

  return (
    <form className="form-container youth-form" onSubmit={handleSubmit} autoComplete="on">
      {/* Honeypot: hidden field to catch bots (should stay empty) */}
      <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Website
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChange}
            autoComplete="off"
            tabIndex={-1}
          />
        </label>
      </div>

      <header>
        <h2>Youth Registration</h2>
        <p className="muted small">Complete the camper information. Fields marked * are required.</p>
      </header>

      <div className="two-col">
        <div className="field">
          <label className="field-label">
            First Name <span className="required">*</span>
          </label>
          <input
            name="firstName"
            className="input"
            value={formData.firstName}
            onChange={handleChange}
            required
            autoComplete="given-name"
            autoCapitalize="words"
            spellCheck="false"
          />
        </div>

        <div className="field">
          <label className="field-label">
            Last Name <span className="required">*</span>
          </label>
          <input
            name="lastName"
            className="input"
            value={formData.lastName}
            onChange={handleChange}
            required
            autoComplete="family-name"
            autoCapitalize="words"
            spellCheck="false"
          />
        </div>
      </div>

      <div className="two-col">
        <div className="field">
          <label className="field-label">
            Date of Birth <span className="required">*</span>
          </label>
          <input
            type="text"
            name="dob"
            className="input"
            value={formData.dob}
            onChange={handleChange}
            required
            placeholder="MM/DD/YYYY"
            inputMode="numeric"
            autoComplete="bday"
            maxLength={10}
          />
        </div>

        <div className="field">
          <label className="field-label">
            Phone <span className="required">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            className="input"
            value={formData.phone}
            onChange={handleChange}
            required
            autoComplete="tel"
            inputMode="tel"
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      <div className="field">
        <label className="field-label">
          Email <span className="required">*</span>
        </label>
        <input
          type="email"
          name="email"
          className="input"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
          placeholder="you@example.com"
        />
      </div>

      <div className="field">
        <label className="field-label">
          Dorm Choice <span className="required">*</span>
        </label>
        <select name="dormChoice" className="select" value={formData.dormChoice} onChange={handleChange} required>
          <option value="">Select</option>
          <option value="girls">Girls dorm</option>
          <option value="boys">Boys dorm</option>
          <option value="parent">Staying with a parent</option>
        </select>
      </div>

      {formData.dormChoice === "parent" && (
        <div className="field">
          <label className="field-label">Staying with (name)</label>
          <input
            name="stayingWith"
            className="input"
            value={formData.stayingWith}
            onChange={handleChange}
            placeholder="Name of adult staying with camper"
          />
        </div>
      )}

      <h3>Emergency Contact 1</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <div className="field">
          <label className="field-label">
            Name <span className="required">*</span>
          </label>
          <input
            name="emergency1Name"
            className="input"
            value={formData.emergency1Name}
            onChange={handleChange}
            required
            autoComplete="name"
            placeholder="First & Last Name"
          />
        </div>

        <div className="field">
          <label className="field-label">
            Phone <span className="required">*</span>
          </label>
          <input
            name="emergency1Phone"
            className="input"
            value={formData.emergency1Phone}
            onChange={handleChange}
            required
            autoComplete="tel"
            inputMode="tel"
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      <h3>Emergency Contact 2</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <div className="field">
          <label className="field-label">Name</label>
          <input
            name="emergency2Name"
            className="input"
            value={formData.emergency2Name}
            onChange={handleChange}
            autoComplete="name"
            placeholder="First & Last Name"
          />
        </div>

        <div className="field">
          <label className="field-label">Phone</label>
          <input
            name="emergency2Phone"
            className="input"
            value={formData.emergency2Phone}
            onChange={handleChange}
            autoComplete="tel"
            inputMode="tel"
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      {/* Allergies question + inline details + acknowledgement */}
      <div className="field">
        <label className="field-label">
          Does the camper have allergies? <span className="required">*</span>
        </label>
        <select
          name="hasAllergies"
          className="select"
          value={formData.hasAllergies}
          onChange={handleChange}
          required
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {formData.hasAllergies === "yes" && (
        <div className="field allergy-block" ref={allergyRef}>
          <label className="field-label">
            Please list allergies <span className="required">*</span>
          </label>
          <input
            name="allergies"
            className="input"
            value={formData.allergies}
            onChange={handleChange}
            required
            placeholder="e.g. Peanuts, Penicillin"
          />

          <div className="allergy-notice" role="note" aria-live="polite" style={{ marginTop: 10 }}>
            <p className="muted" style={{ margin: 0 }}>
              Please note: we cannot guarantee there will not be cross contamination of food allergens.
            </p>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                name="allergyAcknowledged"
                checked={!!formData.allergyAcknowledged}
                onChange={handleChange}
              />
              I acknowledge and agree to the allergy policy above.
            </label>
          </div>
        </div>
      )}

      <div className="field">
        <label className="field-label">
          Does the camper take medications? <span className="required">*</span>
        </label>
        <select
          name="takesMedications"
          className="select"
          value={formData.takesMedications}
          onChange={handleChange}
          required
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {formData.takesMedications === "yes" && (
        <div className="field">
          <label className="field-label">Medications (list)</label>
          <input
            name="medications"
            className="input"
            value={formData.medications}
            onChange={handleChange}
            placeholder="Medication names, dosing notes"
          />
        </div>
      )}

      {showMedWarning && (
        <div className="med-warning" ref={warningRef}>
          <p>
            This camper takes medications and is staying on campus. Please acknowledge per Illinois law, upon arrival to the camp, medications must be turned over to the camp nurse. Please note: we might also reach out to get more information and Dr.'s notes about the medication plan.
          </p>
          <label>
            <input
              type="checkbox"
              checked={medsAcknowledged}
              onChange={(e) => setMedsAcknowledged(e.target.checked)}
            />
            I acknowledge the medication plan
          </label>
        </div>
      )}

      <div className="form-footer">
        <button type="submit" className="btn btn-primary">
          Next
        </button>
      </div>
    </form>
  );
}